import React from 'react';
import { useTranslation } from '@shared/core';
import { BoxProps, Flex, TextV2, TooltipV2 } from '@withjoy/joykit';
import { AccommodationCombined } from '../../Accommodations/Accommodation.types';
import { createDateFromUnformattedString } from '@shared/utils/dateHelpers';
import { Help } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import {
  body1ToParagraphOverride,
  hed2ToSubHeadingOverride,
  hed4ToSubHeadingOverride,
} from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { useScaleGuestSiteFontSize } from '@apps/guest/components/GuestSiteTypographyOverride/hooks/useScaleGuestSiteFontSize';
import { Category } from '@graphql/generated';

interface RoomBlockPricingNonGridProps extends BoxProps {
  accommodation: AccommodationCombined;
}

export const RoomBlockPricingNonGrid = ({ accommodation, ...boxProps }: RoomBlockPricingNonGridProps) => {
  const { strikeoutPricePerNight, pricePerNight, cutoffDate } = accommodation;
  const accTrans = useTranslation('guestSiteCustom').t('accommodations');
  const [accommodationGuestHotelTileFontUpdatesEnabled] = useIsFeatureEnabled('accommodationGuestHotelTileFontUpdates');
  const [fontPackExperimentEnabled] = useIsFeatureEnabled('fontPackExperiment');
  const { scaleFontSize } = useScaleGuestSiteFontSize();

  return (
    <Flex
      justifyContent="space-between"
      alignItems="center"
      width="100%"
      paddingTop={5}
      {...boxProps}
    >
      {cutoffDate && (
        <Flex
          flexDirection="column"
          gap="1"
        >
          {accommodationGuestHotelTileFontUpdatesEnabled ? (
            <>
              <TextV2
                typographyVariant={'body1'}
                color="accentGreen3"
                fontFamily="Inter UI"
                fontWeight={400}
                letterSpacing="-0.15px"
              >
                {accTrans.bookBefore()}
              </TextV2>
              <TextV2
                typographyVariant={'body1'}
                color="mono10"
                fontFamily="Inter UI"
                fontWeight={400}
                letterSpacing="-0.15px"
              >
                {createDateFromUnformattedString(cutoffDate)?.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })}
              </TextV2>
            </>
          ) : (
            <>
              <GuestSiteTypographyOverride
                override={body1ToParagraphOverride}
                defaultFontFamily="Inter UI"
              >
                <TextV2
                  typographyVariant={'body1'}
                  {...(!fontPackExperimentEnabled && { fontFamily: 'Inter UI' })}
                  fontSize={scaleFontSize(pxToRem(15), Category.PARAGRAPH)}
                  color="accentGreen3"
                  fontWeight={400}
                  letterSpacing="-0.15px"
                >
                  {accTrans.bookBefore()}
                </TextV2>
              </GuestSiteTypographyOverride>
              <GuestSiteTypographyOverride
                override={body1ToParagraphOverride}
                defaultFontFamily="Inter UI"
              >
                <TextV2
                  typographyVariant={'body1'}
                  {...(!fontPackExperimentEnabled && { fontFamily: 'Inter UI' })}
                  fontSize={scaleFontSize(pxToRem(15), Category.PARAGRAPH)}
                  color="mono14"
                  fontWeight={400}
                  letterSpacing="-0.15px"
                >
                  {createDateFromUnformattedString(cutoffDate)?.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })}
                </TextV2>
              </GuestSiteTypographyOverride>
            </>
          )}
        </Flex>
      )}
      <Flex
        flexDirection="column"
        gap="1"
      >
        <Flex
          alignItems="center"
          gap={1}
        >
          {accommodationGuestHotelTileFontUpdatesEnabled ? (
            <TextV2
              typographyVariant={'body1'}
              fontFamily="Inter UI"
              color="accentGreen3"
              fontWeight={400}
              textAlign="right"
            >
              {accTrans.groupRate()}
            </TextV2>
          ) : (
            <GuestSiteTypographyOverride
              override={hed4ToSubHeadingOverride}
              defaultFontFamily="Inter UI"
            >
              <TextV2
                typographyVariant={'body1'}
                color="accentGreen3"
                fontWeight={400}
                textAlign="right"
              >
                {accTrans.groupRate()}
              </TextV2>
            </GuestSiteTypographyOverride>
          )}
          <TooltipV2
            content={accTrans.groupRateTooltip()}
            onMouseEnterDelay={500}
            placement={'topEnd'}
            overrides={{
              Content: {
                props: {
                  backgroundColor: 'white',
                  color: 'mono14',
                  maxWidth: '256px',
                  typographyVariant: accommodationGuestHotelTileFontUpdatesEnabled ? 'label2' : undefined,
                },
              },
              Arrow: { props: { backgroundColor: 'white' } },
            }}
          >
            <Help
              size={'sm'}
              color={'accentGreen3'}
              title={undefined}
            />
          </TooltipV2>
        </Flex>
        <Flex
          gap={2}
          justifyContent="flex-end"
        >
          {!!strikeoutPricePerNight &&
            (accommodationGuestHotelTileFontUpdatesEnabled ? (
              <TextV2
                typographyVariant={'body3'}
                color="mono10"
                fontFamily="Inter UI"
                fontWeight={400}
                textAlign="right"
                backgroundSize={'100% 1.5px'}
                width={'auto'}
                backgroundImage={'linear-gradient(currentColor, currentColor)'}
                backgroundPosition={'0% 50%'}
                backgroundRepeat={'no-repeat'}
              >
                ${strikeoutPricePerNight.toString()}
              </TextV2>
            ) : (
              <GuestSiteTypographyOverride
                override={hed4ToSubHeadingOverride}
                defaultFontFamily="Inter UI"
              >
                <TextV2
                  typographyVariant={'body3'}
                  {...(!fontPackExperimentEnabled && { fontFamily: 'Inter UI' })}
                  fontSize={scaleFontSize(pxToRem(17), Category.SUB_HEADING)}
                  color="mono10"
                  fontWeight={400}
                  textAlign="right"
                  backgroundSize={'100% 1.5px'}
                  width={'auto'}
                  backgroundImage={'linear-gradient(currentColor, currentColor)'}
                  backgroundPosition={'0% 50%'}
                  backgroundRepeat={'no-repeat'}
                >
                  ${strikeoutPricePerNight.toString()}
                </TextV2>
              </GuestSiteTypographyOverride>
            ))}
          {accommodationGuestHotelTileFontUpdatesEnabled ? (
            <TextV2
              typographyVariant={'hed2'}
              color="mono14"
              fontFamily="Inter UI"
              fontWeight={600}
              textAlign="right"
              letterSpacing="-0.272px"
              textTransform="none"
            >
              ${accTrans.pricePerNight({ price: pricePerNight })}
            </TextV2>
          ) : (
            <GuestSiteTypographyOverride
              override={hed2ToSubHeadingOverride}
              defaultFontFamily="Inter UI"
            >
              <TextV2
                typographyVariant={'hed2'}
                {...(!fontPackExperimentEnabled && { fontFamily: 'Inter UI' })}
                fontSize={scaleFontSize(pxToRem(17), Category.SUB_HEADING)}
                color="mono14"
                fontWeight={600}
                textAlign="right"
                letterSpacing="-0.272px"
                textTransform="none"
              >
                ${accTrans.pricePerNight({ price: pricePerNight })}
              </TextV2>
            </GuestSiteTypographyOverride>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};
